import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocalStorageInformation } from '../app.component';
import { normalizeCityName } from '../../shared/city-utils';

@Injectable({
  providedIn: 'root'
})
export class ServerCacheService {
  private baseUrl = '/api/weather';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: any
  ) { }

  // Replace getStoredCurrentWeatherInfoFromLocalStorage
  getStoredCurrentWeatherInfoFromServer(cityName: string): Observable<LocalStorageInformation | undefined> {
    // Don't make HTTP calls during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return of(undefined);
    }

    const key = normalizeCityName(cityName) || cityName;
    return this.http.get<LocalStorageInformation>(`${this.baseUrl}/current/${key}`)
      .pipe(
        catchError(error => {
          console.log(`No cached weather data found for ${cityName}`);
          return of(undefined);
        })
      );
  }

  // Replace getStoredForecasttWeatherInfoFromLocalStorage
  getStoredForecastWeatherInfoFromServer(cityName: string): Observable<LocalStorageInformation | undefined> {
    // Don't make HTTP calls during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return of(undefined);
    }

    const key = normalizeCityName(cityName) || cityName;
    return this.http.get<any>(`${this.baseUrl}/forecast/${key}`)
      .pipe(
        map(response => {
          if (response && response.forecast) {
            const localData = new LocalStorageInformation();
            localData.weather = {
              forecast: response.forecast,
              forecastYrno: response.forecastYrno,
              refreshtime: response.refreshtime,
              city: normalizeCityName(cityName) || cityName,
              cityID: '', // You might need to pass this
              weather: undefined
            } as any;
            return localData;
          }
          return undefined;
        }),
        catchError(error => {
          console.log(`No cached forecast data found for ${cityName}`);
          return of(undefined);
        })
      );
  }

  // Replace storeCityCurrentWeatherToLocalStorage
  storeCityCurrentWeatherToServer(cityWeatherInformation: LocalStorageInformation): Observable<any> {
    // Don't make HTTP calls during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const cityName = normalizeCityName(cityWeatherInformation.weather.city) || cityWeatherInformation.weather.city;
    return this.http.post(`${this.baseUrl}/cache/${cityName}`, cityWeatherInformation)
      .pipe(
        catchError(error => {
          console.error('Failed to store weather data to server cache:', error);
          return of(null);
        })
      );
  }

  // Replace storeCityForecastWeatherToLocalStorage
  storeCityForecastWeatherToServer(cityWeatherInformation: LocalStorageInformation): Observable<any> {
    // Don't make HTTP calls during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const cityName = normalizeCityName(cityWeatherInformation.weather.city) || cityWeatherInformation.weather.city;
    return this.http.post(`${this.baseUrl}/cache/${cityName}`, cityWeatherInformation)
      .pipe(
        catchError(error => {
          console.error('Failed to store forecast data to server cache:', error);
          return of(null);
        })
      );
  }

  // Get cache status for monitoring
  getCacheStatus(): Observable<any> {
    return this.http.get(`${this.baseUrl}/cache/status`)
      .pipe(
        catchError(error => {
          console.error('Failed to get cache status:', error);
          return of({ error: 'Failed to get cache status' });
        })
      );
  }

  // Helper method to convert async to sync calls (if needed for compatibility)
  getStoredCurrentWeatherInfoSync(cityName: string): LocalStorageInformation | undefined {
    // This is a fallback method for immediate synchronous access
    // In practice, you should refactor calling code to use Observable patterns
    console.warn('Synchronous cache access not supported with server cache. Use Observable methods.');
    return undefined;
  }

  getStoredForecastWeatherInfoSync(cityName: string): LocalStorageInformation | undefined {
    // This is a fallback method for immediate synchronous access
    console.warn('Synchronous cache access not supported with server cache. Use Observable methods.');
    return undefined;
  }
}